import type { ReactNode } from 'react';
import type { ClaveSat, DatosDeEmision, VistaPreviaPrefactura } from '../api/tipos';

/**
 * La factura dibujada como la imprime Master EDI.
 *
 * Vivía dentro de «Crear prefactura», que era donde nació: Karin pidió que
 * la vista previa se pareciera a la factura de verdad —«lo que estoy
 * esperando es que el preview se parezca lo más posible a lo que el
 * contador va a generar después para que no se pierda entre tanta
 * info»—. Se mudó aquí cuando pidió lo mismo para el botón «Ver» de
 * Prefacturas: «quisiera que muestre solo la vista de factura generada
 * aunque sea solo de vista, no me interesa ver el txt».
 *
 * Que sea un solo componente y no dos copias es lo que importa. Son dos
 * pantallas que enseñan el mismo papel en dos momentos, y el día que el
 * formato cambie —una columna, un recuadro— tiene que cambiar en las dos
 * a la vez. Dos dibujos del mismo documento acaban discrepando, y la que
 * se mira antes de generar dejaría de ser la que se mira después.
 *
 * ── Las dos reglas del dibujo ───────────────────────────────────────
 *
 *   1. Está todo lo que la factura imprime, en su sitio. El contador
 *      revisa con el PDF de la vez pasada al lado; si los bloques están
 *      en otro orden o con otros nombres, comparar obliga a buscar cada
 *      dato y ahí es donde se salta uno.
 *
 *   2. No está nada más. Ni una etiqueta de ayuda, ni el objeto de
 *      impuesto, ni una nota explicando el IVA. Todo eso ya se capturó en
 *      el formulario; repetirlo aquí es justo la «tanta info» entre la
 *      que se pierde.
 *
 * ── Un solo dibujo para los tres tipos ──────────────────────────────
 *
 * Iguala, correctiva y libre se imprimen exactamente igual. Se comprobó
 * contra las tres facturas timbradas que mandó Karin —la E-70847, la
 * E-70843 y la E-70895—: mismos recuadros, mismo orden, mismas ocho
 * columnas. Lo único que cambia es qué trae cada una dentro:
 *
 *   iguala      un renglón, unidad SERVICIO, y la tienda dicha en la
 *               propia descripción («… BOCHIL RIO - 1237»).
 *   correctiva  un renglón por concepto cotizado, unidad PZA.
 *   libre       lo que se capture; si es pieza, con su código de barras
 *               dentro de la descripción.
 *
 * Por eso aquí no hay ninguna rama por tipo, y no es un descuido: meter
 * una sería inventar una diferencia que el formato no tiene, y que
 * después habría que mantener.
 */

/** «2026-08-17» y no «17/8/2026 0:00»: la factura escribe la fecha sola. */
export function dia(iso: string | null | undefined): string {
  if (!iso) return '';

  return iso.slice(0, 10);
}

/** Con hora, para la expedición: el SAT sella el segundo. */
export function instante(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? String(iso) : `${dia(d.toISOString())} ${
    d.toLocaleTimeString('es-MX', { hour12: false })}`;
}

/** «AV. PASEO DE LAS ESTRELLAS No. 162 Col. LAS CUMBRES», como lo imprime. */
export function domicilioEnRenglones(d: {
  calle: string; numeroExterior: string; numeroInterior?: string;
  colonia: string; municipio: string; localidad: string;
  estado: string; codigoPostal: string; pais: string;
}): string[] {
  const numero = [d.numeroExterior, d.numeroInterior].filter(Boolean).join(' int. ');
  const primero = [
    [d.calle, numero && `No. ${numero}`].filter(Boolean).join(' '),
    d.colonia && `Col. ${d.colonia}`,
  ].filter(Boolean).join('   ');

  const segundo = [
    (d.municipio || d.localidad) && `Del/Mun. ${[d.municipio, d.localidad]
      .filter(Boolean).join(' ')}`,
    d.codigoPostal && `C.P. ${d.codigoPostal}.`,
    d.estado,
    d.pais,
  ].filter(Boolean).join(' ');

  return [primero, segundo].filter(Boolean);
}

/** El nombre de una clave del SAT, o la clave sola si no se conoce. */
export function conNombre(
  opciones: ClaveSat[] | undefined, clave: string | null,
): string | null {
  if (!clave) return null;
  const encontrada = (opciones ?? []).find((o) => o.clave === clave);

  return encontrada ? `${clave} · ${encontrada.nombre}` : clave;
}

/**
 * Un recuadro con su título en barra, como los del papel.
 *
 * La factura de Master EDI dibuja EMISOR, CLIENTE, LUGAR DE ENTREGA,
 * GENERALES y COMERCIALES así, y la vista previa los repite igual a
 * propósito: quien revisa tiene el PDF de la vez pasada al lado.
 */
function Recuadro({ titulo, children, style }: {
  titulo: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{
      border: '1px solid #1f4e9c', borderRadius: 6, overflow: 'hidden', ...style,
    }}>
      <div style={{
        background: '#1f4e9c', color: '#fff', fontWeight: 600,
        fontSize: 11, letterSpacing: 1.5, padding: '3px 8px',
      }}>
        {titulo}
      </div>
      <div style={{ padding: '6px 8px', fontSize: 12 }}>{children}</div>
    </div>
  );
}

/** Etiqueta en negritas y valor al lado, que es como están en el papel. */
function Renglon({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6, lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{etiqueta}</span>
      <span>{children}</span>
    </div>
  );
}

/** Una celda de las columnas GENERALES y COMERCIALES: rótulo arriba, dato abajo. */
function Celda({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div style={{ borderTop: '1px solid #c9d6ea', textAlign: 'center' }}>
      <div style={{ fontWeight: 600, fontSize: 11, padding: '2px 4px' }}>{rotulo}</div>
      <div style={{
        padding: '2px 4px', borderTop: '1px solid #e4eaf3', minHeight: 17,
      }}>
        {valor}
      </div>
    </div>
  );
}

/**
 * Un importe como lo imprime la factura: sin el signo de pesos.
 *
 * En el papel la columna dice «1,000.00» y el signo aparece una sola vez,
 * en la columna «:$» de los totales. Ponerlo en cada renglón llenaría la
 * tabla de símbolos que no están en la hoja contra la que se compara.
 */
export function cifra(valor: number): string {
  return valor.toLocaleString('es-MX', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/** Un renglón del cuadro de totales, con su columna «:$» como en el papel. */
function Total({ rotulo, importe, destacado = false }: {
  rotulo: string;
  importe: number;
  destacado?: boolean;
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8,
      padding: destacado ? '5px 8px' : '4px 8px',
      background: destacado ? '#1f4e9c' : undefined,
      color: destacado ? '#fff' : undefined,
      fontSize: destacado ? 14 : undefined,
    }}>
      <span style={{ fontWeight: destacado ? 600 : undefined }}>{rotulo}</span>
      <span>:$</span>
      <strong style={{ minWidth: 80, textAlign: 'right' }}>{cifra(importe)}</strong>
    </div>
  );
}

/** Un renglón de la caja FACTURA que sólo se llena al timbrar. */
function AlTimbrar({ etiqueta }: { etiqueta: string }) {
  return (
    <div style={{ display: 'flex', gap: 6, lineHeight: 1.5, color: '#9b9b9b' }}>
      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{etiqueta}</span>
      <span style={{
        flex: 1, borderBottom: '1px dotted #d6d6d6', alignSelf: 'flex-end', height: 12,
      }} />
    </div>
  );
}

/**
 * En qué momento se está mirando el comprobante.
 *
 *   borrador  todavía no se genera. El folio que se enseña es el que le
 *             tocaría y puede cambiar, porque entre mirar y generar otra
 *             persona puede emitir la suya.
 *   emitida   la prefactura ya existe y su folio es suyo. Es lo que ve
 *             quien abre «Ver» en la lista.
 */
export type MomentoDelComprobante = 'borrador' | 'emitida';

export function ComprobanteDeLaFactura({
  previa, divisa, sat, momento = 'borrador', uuidFiscal = null,
}: {
  previa: VistaPreviaPrefactura;
  divisa: string;
  sat: DatosDeEmision['sat'] | undefined;
  momento?: MomentoDelComprobante;
  /**
   * El folio fiscal, cuando Master EDI ya lo devolvió.
   *
   * Es el único dato del timbrado que APRISA guarda. La fecha de
   * certificación y las series de los certificados no se piden de
   * vuelta, así que esos renglones siguen en gris incluso en una
   * timbrada: enseñarlos llenos sería inventarlos.
   */
  uuidFiscal?: string | null;
}) {
  const c = previa.comprobante;
  const m = previa.comerciales;
  const r = c.receptor;

  // «601 · General de Ley Personas Morales», como lo imprime la factura.
  // La clave sola obliga a saberse el catálogo de memoria para notar que
  // está mal, y quien revisa no se lo sabe.
  const conSuNombre = (opciones: ClaveSat[] | undefined, clave: string): string =>
    conNombre(opciones, clave || null) ?? '';

  return (
    <div style={{ fontSize: 12, background: '#fff', color: '#1a1a1a' }}>
      {/* El folio todavía no es suyo, y decirlo evita que alguien lo
          apunte y después no cuadre: entre mirar y generar, otra persona
          puede emitir la suya. Una vez generada el folio ya es suyo, así
          que el aviso sobra y sólo estorbaría. */}
      {momento === 'borrador' && (
        <div className="aviso" style={{ marginBottom: 10 }}>
          Le tocaría el folio <strong>{c.serie} - {c.folio}</strong>. Todavía no
          está tomado: se aparta al generar la prefactura.
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)',
        gap: 10, alignItems: 'start',
      }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <img
            src="/logo.png"
            alt="APRISA Mantenimiento Industrial"
            style={{ height: 40, objectFit: 'contain', objectPosition: 'left' }}
          />

          <Recuadro titulo="E M I S O R">
            <Renglon etiqueta="R.F.C. :">{c.emisor.rfc}</Renglon>
            <Renglon etiqueta="Nombre :">{c.emisor.nombre}</Renglon>
            <Renglon etiqueta="Régimen Fiscal :">
              {conSuNombre(sat?.regimenesFiscales, c.emisor.regimen)}
            </Renglon>
          </Recuadro>

          <Recuadro titulo="C L I E N T E">
            <Renglon etiqueta="R.F.C. :">{r?.rfc ?? ''}</Renglon>
            <Renglon etiqueta="Nombre :">{r?.nombre ?? ''}</Renglon>
            <Renglon etiqueta="Régimen Fiscal :">
              {conSuNombre(sat?.regimenesFiscales, r?.regimen ?? '')}
            </Renglon>
            <Renglon etiqueta="Domicilio Fiscal :">
              <span>
                {(r ? domicilioEnRenglones(r) : []).map((linea) => (
                  <span key={linea} style={{ display: 'block' }}>{linea}</span>
                ))}
              </span>
            </Renglon>
            <Renglon etiqueta="Uso Cfdi :">
              {conSuNombre(sat?.usosCfdi, c.usoCfdi)}
            </Renglon>
          </Recuadro>

          {/* Vacío, como en el papel.

              El EAN de la tienda sí viaja en el archivo —etiqueta EANENT—
              pero la representación impresa no lo saca: en las tres
              facturas timbradas que hay de muestra este recuadro está en
              blanco. Cuando la tienda importa, va dentro de la
              descripción del concepto, que es donde el cliente la lee.

              El recuadro se queda porque en el papel está. */}
          <Recuadro titulo="LUGAR DE ENTREGA">
            <div style={{ minHeight: 34 }} />
          </Recuadro>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <Recuadro titulo="FACTURA">
            <Renglon etiqueta="Serie / Folio">{c.serie} - {c.folio}</Renglon>
            <Renglon etiqueta="Efecto de comprobante">I Ingreso</Renglon>
            {uuidFiscal
              ? <Renglon etiqueta="Folio Fiscal">
                <span className="mono">{uuidFiscal}</span>
              </Renglon>
              : <AlTimbrar etiqueta="Folio Fiscal" />}
            <Renglon etiqueta="Fecha Expedición y Lugar">
              {instante(c.fechaExpedicion)} {c.emisor.codigoPostal}
            </Renglon>
            {/* Siguen en gris aunque esté timbrada: de la respuesta de
                Master EDI sólo se guarda el UUID, así que estos tres no
                se saben. */}
            <AlTimbrar etiqueta="Fecha y Hora de Certificación" />
            <AlTimbrar etiqueta="No Serie Certificado SAT" />
            <AlTimbrar etiqueta="No Serie del CSD" />
            {/* Sin gris ni línea: en las tres timbradas sale vacío, así
                que no es algo que esté por llegar. */}
            <Renglon etiqueta="# Autorización Cfdi">{''}</Renglon>
          </Recuadro>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Recuadro titulo="GENERALES" style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 11 }}>Moneda</div>
              <div>{c.moneda}</div>
              <Celda rotulo="Tipo Cambio" valor="1" />
              <Celda
                rotulo="Forma de Pago"
                valor={[c.claveFormaPago, c.formaPago].filter(Boolean).join(' ')}
              />
              <Celda
                rotulo="Método de Pago"
                valor={[c.codigoMetodoPago, c.metodoPago].filter(Boolean).join(' ')}
              />
              <Celda rotulo="Condiciones de Pago" valor={c.condicionesPago} />
              <div style={{ borderTop: '1px solid #c9d6ea', paddingTop: 2 }}>
                <span style={{ fontWeight: 600 }}>Cuenta Pago : </span>{c.cuentaPago}
              </div>
            </Recuadro>

            <Recuadro titulo="COMERCIALES" style={{ textAlign: 'center' }}>
              {/* El rótulo dice «y Fecha» porque así lo imprime el formato
                  del cliente, pero debajo va sólo el número.

                  Karin: «dice el primer campo "Orden de compra y fecha",
                  sin embargo la fecha no la agregamos en ninguna de las
                  facturas». Tenía razón, y el error era de esta vista: el
                  archivo que se le manda a Master EDI no lleva ningún
                  campo con la fecha de la OC —se puede ver en
                  `master-edi.ts`—, así que la factura impresa sale sin
                  ella. Dibujarla aquí hacía que la vista previa prometiera
                  un dato que el papel no iba a traer, que es justo lo
                  contrario de para lo que sirve una vista previa. */}
              <div style={{ fontWeight: 600, fontSize: 11 }}>Orden Compra y Fecha</div>
              <div>{c.numeroOrdenCompra}</div>
              <Celda rotulo="Contra-Recibo y Fecha" valor={m.contraRecibo ?? ''} />
              <div style={{ borderTop: '1px solid #e4eaf3' }}>
                {dia(m.contraReciboFecha ?? c.fechaContraRecibo)}
              </div>
              <Celda rotulo="Número Proveedor" valor={m.numeroProveedor ?? ''} />
              <Celda rotulo="Número Cliente" valor={m.numeroCliente ?? ''} />
              <Celda rotulo="Número Agente" valor={m.numeroAgente ?? ''} />
            </Recuadro>
          </div>
        </div>
      </div>

      {/* Las mismas ocho columnas del papel, en el mismo orden. «Cve Art»
          va vacía: el formato la imprime y el sistema no guarda clave de
          artículo del cliente. Se deja a la vista para que la diferencia
          se note, en vez de descubrirla el día que un cliente la pida.

          El UPC va dentro de la descripción y no en su propia columna,
          porque así lo escribe el archivo y así sale impreso. */}
      <table style={{
        width: '100%', marginTop: 10, fontSize: 11, tableLayout: 'fixed',
      }}>
        <thead>
          <tr>
            <th className="numero" style={{ width: '9%' }}>Cantidad</th>
            <th style={{ width: '11%' }}>CveSat</th>
            <th style={{ width: '9%' }}>Cve Art</th>
            <th style={{ width: '8%' }}>Cve Uni</th>
            <th style={{ width: '8%' }}>Unid.</th>
            <th>Descripción</th>
            <th className="numero" style={{ width: '13%' }}>Valor Unitario</th>
            <th className="numero" style={{ width: '13%' }}>Importe</th>
          </tr>
        </thead>
        <tbody>
          {c.conceptos.map((x, i) => (
            <tr key={`${x.descripcion}-${i}`} style={{ verticalAlign: 'top' }}>
              <td className="numero">{x.cantidad.toFixed(2)}</td>
              <td className="mono">{x.claveProdServ}</td>
              <td />
              <td className="mono">{x.claveUnidad}</td>
              <td>{x.unidad}</td>
              <td>
                {x.descripcion}
                {x.codigoUpc && ` Código Upc/Ean: ${x.codigoUpc}`}
                {/* Las observaciones se imprimen aquí, bajo la descripción
                    del primer renglón y con un espacio de por medio. Así
                    salen en la iguala real —«PEDIDO: RECIBO:»— y no en un
                    bloque aparte: el formato no tiene uno. */}
                {i === 0 && c.observaciones && (
                  <div style={{ marginTop: 12 }}>{c.observaciones}</div>
                )}
              </td>
              <td className="numero">{cifra(x.valorUnitario)}</td>
              <td className="numero">{cifra(x.cantidad * x.valorUnitario)}</td>
            </tr>
          ))}
          {/* El cuerpo de la tabla ocupa media hoja aunque haya un solo
              renglón. No es adorno: es lo que hace que los totales caigan
              donde caen en el papel, que es donde el ojo los busca. */}
          <tr style={{ height: 150 }}>
            <td colSpan={8} style={{ borderBottom: 'none' }} />
          </tr>
        </tbody>
      </table>

      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)',
        gap: 10, marginTop: 10, alignItems: 'start',
      }}>
        {/* Sólo la cantidad con letra. Las observaciones ya salieron
            dentro del concepto, que es donde las pone el formato. */}
        <div style={{ border: '1px solid #c9d6ea', borderRadius: 6, padding: '6px 8px' }}>
          <div style={{ fontWeight: 600, fontSize: 11 }}>Cantidad con Letra</div>
          <div>{c.totalConLetra}</div>
        </div>

        <div style={{ border: '1px solid #c9d6ea', borderRadius: 6, overflow: 'hidden' }}>
          <Total rotulo="Sub-Total" importe={previa.totales.subtotal} />
          <Total rotulo="IVA Trasladado % 16.00" importe={previa.totales.iva} />
          <Total rotulo="TOTAL" importe={previa.totales.total} destacado />
        </div>
      </div>

      {/* El pie dice en qué punto está esto, que es lo que decide si
          sirve para mandárselo al cliente. Una timbrada y una que
          todavía no lo está se ven idénticas, y confundirlas es mandar
          como comprobante algo que el SAT no ha sellado. */}
      <div style={{
        marginTop: 10, padding: '5px 8px',
        background: uuidFiscal ? '#1a7f4b' : '#1f4e9c', color: '#fff',
        borderRadius: 6, textAlign: 'center', fontSize: 11,
      }}>
        {uuidFiscal
          ? <>Timbrada. El folio fiscal y los sellos los puso Master EDI</>
          : <>Todavía no es un CFDI: el folio fiscal y los sellos los pone Master EDI
            al timbrar</>}
        {divisa === 'MXN' ? '' : `. Los importes van en ${divisa}`}.
      </div>
    </div>
  );
}
